import { Alert, Button, Drawer, Input, Spin } from "@arco-design/web-react";
import { Ban, MessageSquareText, SquarePen, Star } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { groupItinerarySuggestions } from "../runtime/groupItinerarySuggestions";
import {
  useItineraryInternalInteractions,
  type ItineraryInternalInteractionRepositories,
} from "../runtime/useItineraryInternalInteractions";
import type {
  ItineraryInternalInteractions,
  ItineraryInternalMarkResult,
  ItineraryMarkType,
  ItinerarySuggestion,
} from "../types";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";

type Props = {
  itineraryId: string;
  enabled: boolean;
  loadInternalInteractions?: (itineraryId: string) => Promise<ItineraryInternalInteractions>;
  toggleInternalMark?: (
    itineraryId: string,
    markType: ItineraryMarkType,
  ) => Promise<ItineraryInternalMarkResult>;
  createSuggestion?: (
    itineraryId: string,
    content: string,
    parentId?: string | null,
  ) => Promise<ItinerarySuggestion>;
};

/** 渲染仅供内部人员使用的建议、拉黑、收藏和评论操作区。 */
export function ItineraryInternalActions({
  itineraryId,
  enabled,
  loadInternalInteractions,
  toggleInternalMark,
  createSuggestion,
}: Props) {
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [suggestionDraft, setSuggestionDraft] = useState("");
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!enabled) {
      setSuggestionOpen(false);
      setReplyingToId(null);
      setReplyDrafts({});
    }
  }, [enabled]);
  const repositories = useMemo<ItineraryInternalInteractionRepositories | undefined>(() => (
    loadInternalInteractions && toggleInternalMark && createSuggestion
      ? {
          load: loadInternalInteractions,
          toggle: toggleInternalMark,
          createSuggestion,
        }
      : undefined
  ), [createSuggestion, loadInternalInteractions, toggleInternalMark]);
  const runtime = useItineraryInternalInteractions(itineraryId, enabled, repositories);
  const interactions = runtime.interactions ?? {
    suggestionCount: 0,
    blacklistCount: 0,
    favoriteCount: 0,
    blacklistedByMe: false,
    favoritedByMe: false,
    suggestions: [],
  };
  const suggestionThreads = useMemo(
    () => groupItinerarySuggestions(interactions.suggestions),
    [interactions.suggestions],
  );
  const normalizedDraft = suggestionDraft.trim();
  const activeReplyDraft = replyingToId ? replyDrafts[replyingToId]?.trim() ?? "" : "";

  const submitSuggestion = async () => {
    if (!normalizedDraft || runtime.submittingSuggestion) return;
    try {
      await runtime.createSuggestion(normalizedDraft);
      setSuggestionDraft("");
    } catch {
      // 错误由抽屉内稳定提示呈现，保留输入供用户重试。
    }
  };

  const submitReply = async (parentId: string) => {
    const content = replyDrafts[parentId]?.trim() ?? "";
    if (!content || runtime.submittingSuggestion) return;
    try {
      await runtime.createSuggestion(content, parentId);
      setReplyDrafts((current) => {
        const next = { ...current };
        delete next[parentId];
        return next;
      });
      setReplyingToId((current) => (current === parentId ? null : current));
    } catch {
      // 错误由抽屉内稳定提示呈现，保留输入供用户重试。
    }
  };

  return (
    <>
      <div className="itinerary-customer-actions" aria-label="行程内部操作">
        <button
          type="button"
          aria-label={`建议 ${interactions.suggestionCount}`}
          onClick={() => setSuggestionOpen(true)}
        >
          <SquarePen size={16} aria-hidden="true" />
          <span>建议</span>
          <small>{interactions.suggestionCount}</small>
        </button>
        <button
          type="button"
          className={interactions.blacklistedByMe ? "is-selected" : undefined}
          aria-label={`拉黑 ${interactions.blacklistCount}`}
          aria-pressed={interactions.blacklistedByMe}
          disabled={runtime.loading || Boolean(runtime.pendingMark)}
          onClick={() => void runtime.toggle("blacklist")}
        >
          <Ban size={16} aria-hidden="true" />
          <span>拉黑</span>
          <small>{interactions.blacklistCount}</small>
        </button>
        <button
          type="button"
          className={interactions.favoritedByMe ? "is-selected" : undefined}
          aria-label={`收藏 ${interactions.favoriteCount}`}
          aria-pressed={interactions.favoritedByMe}
          disabled={runtime.loading || Boolean(runtime.pendingMark)}
          onClick={() => void runtime.toggle("favorite")}
        >
          <Star size={16} aria-hidden="true" />
          <span>收藏</span>
          <small>{interactions.favoriteCount}</small>
        </button>
        <button type="button" aria-label="评论 0" title="评论功能暂未接入">
          <MessageSquareText size={16} aria-hidden="true" />
          <span>评论</span>
          <small>0</small>
        </button>
      </div>
      <Drawer
        className="itinerary-suggestion-drawer"
        title="行程建议"
        closeIcon={<ItineraryDialogCloseButton label="关闭行程建议" />}
        visible={enabled && suggestionOpen}
        width={440}
        zIndex={1301}
        footer={null}
        onCancel={(event) => {
          event.stopPropagation();
          setSuggestionOpen(false);
        }}
      >
        <section
          role="dialog"
          aria-modal="true"
          aria-label="行程建议"
          className="itinerary-suggestion-panel"
        >
          {runtime.loadError || runtime.toggleError ? <div className="itinerary-suggestion-alerts">
            {runtime.loadError ? <Alert
              type="error"
              showIcon
              content="建议暂时无法加载，请稍后重试。"
              action={<Button size="small" onClick={() => void runtime.reload()}>重试</Button>}
            /> : null}
            {runtime.toggleError ? <Alert type="error" showIcon content="标记操作失败，请重试。" /> : null}
          </div> : null}
          <div className="itinerary-suggestion-body">
            {runtime.loading ? <Spin block tip="正在加载建议" /> : (
              <div className="itinerary-suggestion-list" aria-label="建议历史">
                {suggestionThreads.length ? suggestionThreads.map((thread) => (
                  <article key={thread.id} className="itinerary-suggestion-thread">
                    <SuggestionMessage suggestion={thread} />
                    <div className="itinerary-suggestion-thread-actions">
                      <Button
                        type="text"
                        size="mini"
                        aria-label={`回复 ${thread.authorName}`}
                        onClick={() => setReplyingToId((current) => (
                          current === thread.id ? null : thread.id
                        ))}
                      >
                        回复
                      </Button>
                    </div>
                    {thread.replies.length ? <div className="itinerary-suggestion-replies">
                      {thread.replies.map((reply) => (
                        <SuggestionMessage key={reply.id} suggestion={reply} compact />
                      ))}
                    </div> : null}
                    {replyingToId === thread.id ? <div className="itinerary-suggestion-reply-editor">
                      {runtime.suggestionError ? <Alert type="error" showIcon content="回复提交失败，输入内容已保留。" /> : null}
                      <Input.TextArea
                        aria-label={`回复 ${thread.authorName}`}
                        value={replyDrafts[thread.id] ?? ""}
                        maxLength={2000}
                        showWordLimit
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        placeholder={`回复 ${thread.authorName}`}
                        onChange={(value) => setReplyDrafts((current) => ({
                          ...current,
                          [thread.id]: value,
                        }))}
                      />
                      <div className="itinerary-suggestion-reply-actions">
                        <Button
                          size="mini"
                          onClick={() => {
                            setReplyingToId(null);
                            setReplyDrafts((current) => {
                              const next = { ...current };
                              delete next[thread.id];
                              return next;
                            });
                          }}
                        >
                          取消
                        </Button>
                        <Button
                          type="primary"
                          size="mini"
                          aria-label={`提交回复 ${thread.authorName}`}
                          loading={runtime.submittingSuggestion}
                          disabled={!activeReplyDraft}
                          onClick={() => void submitReply(thread.id)}
                        >
                          提交回复
                        </Button>
                      </div>
                    </div> : null}
                  </article>
                )) : <p className="itinerary-suggestion-empty">暂无建议，可以在下方提交第一条意见。</p>}
              </div>
            )}
          </div>
          <div className="itinerary-suggestion-editor">
            {runtime.suggestionError && !replyingToId ? <Alert type="error" showIcon content="建议提交失败，输入内容已保留。" /> : null}
            <Input.TextArea
              aria-label="建议内容"
              value={suggestionDraft}
              maxLength={2000}
              showWordLimit
              autoSize={{ minRows: 3, maxRows: 6 }}
              placeholder="请输入对本行程的建议"
              onChange={setSuggestionDraft}
            />
            <div className="itinerary-suggestion-editor-actions">
              <Button
                type="primary"
                aria-label="提交建议"
                loading={runtime.submittingSuggestion}
                disabled={!normalizedDraft}
                onClick={() => void submitSuggestion()}
              >
                提交建议
              </Button>
            </div>
          </div>
        </section>
      </Drawer>
    </>
  );
}

/** 渲染单条建议或回复消息。 */
function SuggestionMessage({
  suggestion,
  compact = false,
}: {
  suggestion: ItinerarySuggestion;
  compact?: boolean;
}) {
  return (
    <div className={compact ? "itinerary-suggestion-message is-compact" : "itinerary-suggestion-message"}>
      <header>
        <div className="itinerary-suggestion-author">
          <strong>{suggestion.authorName}</strong>
          {suggestion.authorDeptName ? <span className="itinerary-suggestion-dept">{suggestion.authorDeptName}</span> : null}
        </div>
        <time dateTime={suggestion.createdAt}>{formatSuggestionTime(suggestion.createdAt)}</time>
      </header>
      <p>{suggestion.content}</p>
    </div>
  );
}

/** 将后端北京时间字符串格式化为紧凑的内部协作时间。 */
function formatSuggestionTime(value: string): string {
  const normalized = value.replace("T", " ").replace(/\.\d+$/, "");
  return normalized.length >= 16 ? normalized.slice(0, 16) : normalized;
}
